// src/pages/AuthCallback.tsx
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../config";
import { setAuthToken } from "../authToken";
import { useAuth } from "../context/AuthContext";

function getParam(src: string, name: string): string | null {
  const params = new URLSearchParams(src);
  return params.get(name);
}

function extractTokenFromUrl(): string | null {
  const { search, hash } = window.location;
  // Prefer explicit ?token= or #token=
  const direct =
    getParam(search, "token") ||
    getParam(hash.replace(/^#/, ""), "token");
  if (direct) return direct;

  // Fallbacks some providers use
  const idToken =
    getParam(search, "id_token") ||
    getParam(hash.replace(/^#/, ""), "id_token");
  if (idToken) return idToken;

  const accessToken =
    getParam(search, "access_token") ||
    getParam(hash.replace(/^#/, ""), "access_token");
  if (accessToken) return accessToken;

  return null;
}

async function exchangeCodeForToken(code: string, state?: string | null) {
  const res = await fetch(`${API_BASE}/auth/callback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // send state if you use it server-side
    body: JSON.stringify({ code, state: state || undefined }),
    // include if your backend sets cookies during exchange (harmless otherwise)
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `OAuth exchange failed (${res.status})`);
  }
  return res.json() as Promise<{ token?: string }>;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setToken } = useAuth();

  // Memoize URL parts once
  const { search, hash } = useMemo(
    () => ({ search: window.location.search, hash: window.location.hash }),
    []
  );

  useEffect(() => {
    let aborted = false;

    (async () => {
      try {
        // 1) Direct token in URL (query or hash)
        const directToken = extractTokenFromUrl();
        if (directToken) {
          setAuthToken(directToken);   // persist (localStorage + cookie)
          setToken(directToken);       // update context immediately
          if (!aborted) navigate("/dashboard", { replace: true });
          return;
        }

        // 2) OAuth "code" → exchange with backend for JWT
        const code = getParam(search, "code");
        if (code) {
          const state = getParam(search, "state");
          const data = await exchangeCodeForToken(code, state);
          if (data?.token) {
            setAuthToken(data.token);
            setToken(data.token);
            if (!aborted) navigate("/dashboard", { replace: true });
            return;
          }
          throw new Error("No token returned from exchange");
        }

        // 3) Nothing we can use → send user to login
        navigate("/login", { replace: true });
      } catch {
        // On any failure, bounce to login with a friendly flag
        if (!aborted) navigate("/login?error=signin_failed", { replace: true });
      }
    })();

    return () => {
      aborted = true;
    };
  }, [navigate, search, hash, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-700">Signing you in…</p>
    </div>
  );
}
